import { Rect, Element as SVGElement, Svg } from '@svgdotjs/svg.js';

export const SVG_RED = '#e32b2b';
export const SVG_PIN_BLACK = '#191956ff';
export const SVG_PIN_AURA = '#211e1ea2';
export const SVG_YELLOW = '#f8dd68';
export const SVG_GREEN = '#0f0';

export function SvgHelper(svg: Svg) {
  const _animationRunners = new Map<string, any>();

  function pulse(element: SVGElement | undefined, id: string): void {
    if (!element?.node) return;

    const runner = element.animate(800).ease('<>').transform({ scale: 1.3 }).animate(800).ease('<>').transform({ scale: 1.0 });

    if (runner && runner.after) {
      runner.after(function () {
        if (element && element.node && element.node.parentNode) {
          pulse(element, id);
        }
      });
    }

    _animationRunners.set(id, runner);
  }

  function pulseStop(element: SVGElement | undefined): void {
    const elementId = element?.id();
    if (elementId && _animationRunners.has(elementId)) {
      const runner = _animationRunners.get(elementId);
      if (runner) {
        try {
          if (typeof runner.finish === 'function') {
            runner.finish();
          } else if (typeof runner.stop === 'function') {
            runner.stop();
          }
        } catch {
          // Ignore
        }

        _animationRunners.delete(elementId);
      }
    }

    if (svg.node && svg.node.parentNode) {
      svg.transform({
        scale: 1,
        rotate: 0,
        translate: [0, 0],
      });
    }
  }

  function highlightElement(svg: Svg | undefined, elementToHighight: SVGElement, withBorder = false): Rect | undefined {
    if (!svg) {
      console.warn('[SvgHelper] No SVG provided to highlight element in.');
      return;
    }

    if (!elementToHighight) {
      console.warn('[SvgHelper] No element to highlight provided.');
      return;
    }

    if (!elementToHighight.rbox) {
      console.warn('[SvgHelper] Element has no rbox.', elementToHighight);
      return;
    }
    try {
      const bbox = elementToHighight.rbox(svg);
      const rect = svg.rect(bbox.width, bbox.height).move(bbox.x, bbox.y);
      if (rect) {
        rect.attr({
          fill: SVG_YELLOW,
          'fill-opacity': 0.2,
          stroke: withBorder ? '#0000ff' : 'none',
          'stroke-width': 2,
          'pointer-events': 'none',
          'data-highlight-of': elementToHighight.id(),
        });
        rect.move(bbox.x, bbox.y);
        // changeStrokeColor(elementToHighight, SVG_GREEN);
      }
      return rect;
    } catch {
      // the rect function doesn't work for invisible elements, so we just catch erros here and do nothing
    }

    return undefined;
  }

  function changeStrokeColor(element: SVGElement | undefined, color: string) {
    if (!element) return;

    const origStrokeColor = element.attr('stroke');
    element.attr('data-origStroke', origStrokeColor);
    element.attr({ stroke: color });
  }

  function resetOrigStrokeColor(element: SVGElement | undefined) {
    if (!element) return;

    const origStrokeColor = element.attr('data-origStroke');
    if (origStrokeColor) {
      element.attr({ stroke: origStrokeColor });
    }
  }

  function makeTextsSharpAndNotInteractive(svg: Svg | undefined) {
    if (!svg) return;

    const list = svg.find('text');
    list.forEach((el: any) => {
      try {
        const formerSettings = el.attr('pointer-events');
        if (formerSettings) {
          return;
        }

        if (!el) {
          return;
        }

        el.attr({
          'pointer-events': 'none',
        });

        const parentNode = el.parent('g');
        if (parentNode) {
          parentNode.attr({
            'pointer-events': 'none',
          });
        }

        if (el.node?.tagName?.toLowerCase() === 'text') {
          el.attr({
            'text-rendering': 'optimizeLegibility',
            'shape-rendering': 'geometricPrecision',
          });
        }
      } catch (error) {
        console.warn('[SvgComponent] Error preventing pointer events:', error);
      }
    });
  }

  function removeAllJs(svg: HTMLElement | undefined) {
    if (!svg) {
      console.warn('[SvgComponent] No SVG element provided to remove JS from.');
      return;
    }

    // Strip out script tags for security reasons
    const scripts = svg.getElementsByTagName('script');
    for (let i = scripts.length - 1; i >= 0; i--) {
      const script = scripts[i];
      script.parentNode?.removeChild(script);
    }

    // remove all events from the SVG element and all its children
    const onEvents = ['onload', 'onclick', 'onmouseover', 'onmouseout', 'onmousedown', 'onmouseup', 'onmousemove', 'onmouseenter', 'onmouseleave'];

    // Remove from root SVG element
    onEvents.forEach((event) => {
      svg.removeAttribute(event);
    });

    // Remove from all child elements
    const allElements = svg.querySelectorAll('*');
    allElements.forEach((el) => {
      onEvents.forEach((event) => {
        el.removeAttribute(event);
      });

      // Also fix invalid height="auto" if present
      if (el.getAttribute('height') === 'auto') {
        el.removeAttribute('height');
      }
      // Also fix invalid width="auto" if present
      if (el.getAttribute('width') === 'auto') {
        el.removeAttribute('width');
      }
    });
  }

  function replaceGenericIds(svgEl: HTMLElement | undefined, uniqueSuffix: string): void {
    if (!svgEl) return;

    const forbiddenIds = ['genericDefs', 'clipPath', 'defs'];
    const attributesWithUrls = ['clip-path', 'mask', 'filter', 'fill', 'stroke', 'href', 'xlink:href'];
    const idMap = new Map<string, string>();

    const elementsWithId = svgEl.querySelectorAll('[id]');

    elementsWithId.forEach((el) => {
      const oldId = el.id;

      forbiddenIds.forEach((forbiddenId) => {
        if (oldId.startsWith(forbiddenId)) {
          const newId = `${oldId}_${uniqueSuffix}`;
          idMap.set(oldId, newId);
          el.id = newId;
        }
      });
    });

    const allElements = svgEl.querySelectorAll('*');
    allElements.forEach((el) => {
      attributesWithUrls.forEach((attr) => {
        const val = el.getAttribute(attr);

        // replace urls
        if (val && val.includes('url(#')) {
          idMap.forEach((newId, oldId) => {
            const ref = `url(#${oldId})`;
            if (val.includes(ref)) {
              el.setAttribute(attr, val.replace(ref, `url(#${newId})`));
            }
          });
        }

        // replace hrefs
        if ((attr === 'href' || attr === 'xlink:href') && val && val.startsWith('#')) {
          idMap.forEach((newId, oldId) => {
            if (val === `#${oldId}`) {
              el.setAttribute(attr, `#${newId}`);
            }
          });
        }
      });
    });
  }

  function removePoint(point: SVGElement | null | undefined): boolean {
    if (!point) return false;

    pulseStop(point);

    requestAnimationFrame(() => {
      if (point.node && point.node.parentNode) {
        point.remove();
      }
    });
    return false;
  }

  function drawRect(svg: Svg | undefined, elementToCopy: SVGElement): Rect | undefined {
    if (!svg) return undefined;

    const padding = 4;
    const bbox = elementToCopy.rbox(svg);
    const rect = svg.rect(bbox.width + padding * 2, bbox.height + padding * 2).move(bbox.x, bbox.y);
    if (rect) {
      rect.attr({
        fill: 'none',
        stroke: SVG_RED,
        'stroke-width': 1,
        'stroke-dasharray': '1,2',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        rx: 4, // rounded corners
        ry: 4,
        'pointer-events': 'none',
      });
      rect.move(bbox.x - padding, bbox.y - padding);
    }
    return rect;
  }

  function drawPoint(svg: Svg | undefined, x: number, y: number, color: string): SVGElement | undefined {
    if (!svg) return undefined;

    const radius = 4;

    try {
      const pointGroup = svg.group().front();
      if (!pointGroup) return undefined;

      const pointId = `point-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      pointGroup.attr({
        id: pointId,
        'pointer-events': 'none',
        'data-type': 'connector-point',
      });

      pointGroup
        .circle(radius * 2)
        .fill({ color, opacity: 0.8 })
        .stroke('none')
        .center(x, y);

      pointGroup
        .circle(radius * 4)
        .fill('none')
        .stroke({ color: SVG_PIN_AURA, width: 0.5 })
        .center(x, y);

      pointGroup
        .circle(radius * 5.5)
        .fill('none')
        .stroke({ color: SVG_PIN_AURA, width: 0.5 })
        .center(x, y);

      pointGroup.transform({});

      pulse(pointGroup, pointId);

      return pointGroup;
    } catch (error) {
      console.error('[SvgComponent] Error creating point:', error);
      return undefined;
    }
  }

  function preventTextSelection(svg: Svg | undefined): void {
    const node = svg?.node;
    if (node) {
      // Prevents text selection when user releases mouse outside SVG
      if (node.style) {
        node.style.setProperty('-webkit-touch-callout', 'none');
        node.style.setProperty('-webkit-tap-highlight-color', 'transparent');
      }

      node.style.userSelect = 'none';
      const style = node.style as any;
      if (style.webkitUserSelect !== undefined) style.webkitUserSelect = 'none';
      if (style.mozUserSelect !== undefined) style.mozUserSelect = 'none';
      if (style.msUserSelect !== undefined) style.msUserSelect = 'none';
    }
  }

  function redraw(svg: Svg | undefined) {
    const node = svg?.node;
    if (node) {
      requestAnimationFrame(() => {
        node.style.display = 'none';
        void node.getBoundingClientRect();
        node.style.display = '';
      });
    }
  }

  return {
    pulse,
    pulseStop,
    highlightElement,
    changeStrokeColor,
    resetOrigStrokeColor,
    makeTextsSharpAndNotInteractive,
    removeAllJs,
    replaceGenericIds,
    removePoint,
    drawRect,
    drawPoint,
    preventTextSelection,
    redraw,
  };
}
